// src/components/CommuneAccessGuard.tsx

import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Result, Button } from 'antd';
import { useAuthContext } from '../contexts/AuthContext';

interface CommuneAccessGuardProps {
  children: React.ReactNode;
}

/**
 * Protects routes from URL manipulation by communal admins
 * Ensures communal admins can't manually change URLs to access other communes' data
 */
const CommuneAccessGuard: React.FC<CommuneAccessGuardProps> = ({ children }) => {
  const location = useLocation();
  const { 
    isSuperAdmin, 
    isCommunalAdmin, 
    getAssignedCommuneId,
    user 
  } = useAuthContext();

  // Super admins can access everything
  if (isSuperAdmin()) {
    return <>{children}</>;
  }

  // For communal admins, check URL manipulation
  if (isCommunalAdmin()) {
    const assignedCommuneId = getAssignedCommuneId();
    const path = location.pathname;

    // Extract commune ID from URL patterns
    const communeUrlMatch = path.match(/\/communes\/([^\/]+)/);
    const groupUrlMatch = path.match(/\/groups\/([^\/]+)/);

    // If URL contains a specific commune ID, verify it matches assigned commune
    if (communeUrlMatch) {
      const urlCommuneId = communeUrlMatch[1];
      if (urlCommuneId !== assignedCommuneId) {
        return (
          <Result
            status="403"
            title="Accès refusé"
            subTitle="Vous ne pouvez accéder qu'aux données de votre commune assignée."
            extra={
              <div>
                <p>Tentative d'accès à la commune : <strong>{urlCommuneId}</strong></p>
                <p>Votre commune assignée : <strong>{assignedCommuneId}</strong></p>
                <Button 
                  type="primary" 
                  onClick={() => window.location.href = '/dashboard'}
                >
                  Retourner au tableau de bord
                </Button>
              </div>
            }
          />
        );
      }
    }

    // If URL contains a specific group ID, we need to verify it belongs to their commune
    // This would require an API call to verify group ownership, but the API filtering
    // should handle this automatically. We'll log suspicious access attempts.
    if (groupUrlMatch) {
      const urlGroupId = groupUrlMatch[1];
      console.warn(`Communal admin ${user?.id} attempting to access group ${urlGroupId}. API filtering should handle this.`);
    }

    // Check for other forbidden paths
    const forbiddenPaths = [
      '/communes', // Can't access commune list (only super admin)
      '/staff', // Can't access staff management (only super admin)  
      '/reports/communes', // Can't access cross-commune reports
      '/analytics/system' // Can't access system analytics
    ];

    // Exact path matches
    if (forbiddenPaths.includes(path)) {
      return (
        <Result
          status="403"
          title="Accès refusé"
          subTitle="Cette section n'est disponible que pour les super administrateurs."
          extra={
            <Button 
              type="primary" 
              onClick={() => window.location.href = '/dashboard'}
            >
              Retourner au tableau de bord
            </Button>
          }
        />
      );
    }

    // Pattern-based forbidden paths
    const forbiddenPatterns = [
      /^\/communes\/[^\/]+\/edit$/, // Can't edit other communes
      /^\/staff\/create$/, // Can't create staff (super admin only)
      /^\/system/, // Any system-level paths
    ];

    if (forbiddenPatterns.some(pattern => pattern.test(path))) {
      return (
        <Result
          status="403"
          title="Accès refusé"
          subTitle="Vous n'avez pas la permission d'accéder à cette page."
          extra={
            <Button 
              type="primary" 
              onClick={() => window.location.href = '/dashboard'}
            >
              Retourner au tableau de bord
            </Button>
          }
        />
      );
    }
  }

  // All checks passed, render children
  return <>{children}</>;
};

export default CommuneAccessGuard;